import { useState } from 'react';
import { ApiError, careVitalsRecord, recordWeight, residentEscalate, residentTimelineAdd, shiftFluid, type ApiClient } from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { ADLS, EATEN, LEVELS, MEALS, VITALS, adlLine, fieldFlag, mealLine, numOf, residentLabel, vitalFlags, vitalsLine, type PocResident, type VitalKey, type VitalValues } from './pointOfCare';
import { HEAD, NOTE, chip, prim } from './styles';

type Cat = 'vitals' | 'meals' | 'adl';

/** The server's out-of-range keys (care-vitals.ts computeOutOfRange), in words, and the fields each covers. */
const SERVER_RANGE: Record<string, { words: string; fields: VitalKey[] }> = {
  bp: { words: 'Blood pressure out of range', fields: ['sys', 'dia'] },
  hr: { words: 'Pulse out of range', fields: ['pulse'] },
  temp: { words: 'Temperature out of range', fields: ['temp'] },
  spo2: { words: 'Oxygen out of range', fields: ['spo2'] },
};
/** What the server found out of range that the page's own levels did not already flag. */
export function serverOnlyFlags(outOfRange: string[], vals: VitalValues): string[] {
  return outOfRange
    .filter((k) => !SERVER_RANGE[k]?.fields.some((f) => fieldFlag(VITALS.find((v) => v.k === f)!, vals[f])))
    .map((k) => SERVER_RANGE[k]?.words ?? `${k} out of range`);
}
const LIST = 'display:flex;flex-direction:column;background:#FFFFFF;border:1px solid rgba(34,39,46,.12);border-radius:10px;padding:2px 12px;';
const first = (r: PocResident) => String(r.name ?? 'Resident').split(/\s+/)[0];

/**
 * Chart (design pwLayer "chart", point-of-care.js): Vitals, Meals & fluids (everyone at one
 * table in one go) and Daily care, each saved through the routes that store it:
 *
 *   POST /care/vitals              temperature, pulse, BP and oxygen as numbers; the server
 *                                  decides what is out of range, and a flagged reading becomes
 *                                  a worklist task for the charge nurse on shift (assigned
 *                                  residents; the Facility Admin and the charge nurse: any
 *                                  resident of the building)
 *   POST /residents/:id/weight     weight (kept in kg)
 *   POST /residents/:id/timeline   the entry on the resident's chart under your name: every
 *                                  reading (breathing and pain are kept only here), a meal,
 *                                  daily care
 *   POST /overview/shift/fluids    the fluids, so the day's total counts them (same rule)
 *   POST /residents/:id/escalate   a reading out of range: the charge nurse and the
 *                                  administrator on duty are paged to check
 *
 * The server's task waits on the charge nurse's Worklist; the page also asks the people on duty
 * to check now, and it says who was reached.
 */
export function ChartSheet({
  client,
  residents,
  charge,
  meName,
  onClose,
  onToast,
}: {
  client: ApiClient;
  residents: PocResident[];
  charge: { userId: string; name: string | null } | null;
  meName: string;
  onClose: () => void;
  onToast: (t: string) => void;
}) {
  const [cat, setCat] = useState<Cat>('vitals');
  const [sel, setSel] = useState<string[]>([]);
  const [vals, setVals] = useState<VitalValues>({});
  const [meal, setMeal] = useState<string>('Dinner');
  const [eaten, setEaten] = useState('');
  const [ml, setMl] = useState(0);
  const [adl, setAdl] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ err: ApiError; saved: string[] } | null>(null);

  const multi = cat === 'meals';
  const flags = cat === 'vitals' ? vitalFlags(vals) : [];
  const chargeWho = charge?.name ? `${charge.name} (charge)` : 'the charge nurse';
  const ready =
    sel.length > 0 &&
    !busy &&
    (cat === 'vitals' ? VITALS.some((f) => numOf(vals[f.k]) !== null) : cat === 'meals' ? !!eaten || ml > 0 : ADLS.some((a) => adl[a]));
  const pick = (id: string) =>
    setSel((s) => {
      const on = s.includes(id);
      if (multi) return on ? s.filter((x) => x !== id) : [...s, id];
      return on ? [] : [id];
    });

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    const saved: string[] = [];
    const step = async <T,>(label: string, f: () => Promise<T>): Promise<T> => {
      const r = await f();
      saved.push(label);
      return r;
    };
    try {
      if (cat === 'vitals') {
        const r = residents.find((x) => x.id === sel[0])!;
        const n = (k: keyof VitalValues) => numOf(vals[k]) ?? undefined;
        const readings = { tempF: n('temp'), heartRate: n('pulse'), bpSystolic: n('sys'), bpDiastolic: n('dia'), spo2: n('spo2') };
        const stored = Object.values(readings).some((v) => v !== undefined)
          ? await step('the readings', () => careVitalsRecord(client, { body: { residentId: r.id, readings } }))
          : null;
        const lb = numOf(vals.wt);
        if (lb !== null) await step('the weight', () => recordWeight(client, { params: { id: r.id }, body: { weightKg: Math.round((lb / 2.20462) * 10) / 10 } }));
        const all = [...flags, ...serverOnlyFlags(stored?.outOfRange ?? [], vals)];
        await step('the chart entry', () =>
          residentTimelineAdd(client, { params: { id: r.id }, body: { body: `Vitals: ${vitalsLine(vals)}`, kind: 'vitals', discipline: 'care_staff', ...(all.length ? { severity: 'high' } : {}) } }),
        );
        if (all.length) {
          // The answer names who was paged (POST /residents/:id/escalate in v3/residents/clinical.ts).
          const e = (await step('the request to check', () => residentEscalate(client, { params: { id: r.id }, body: { text: `Check ${r.name ?? 'this resident'}: ${all.join(', ')}. Charted by ${meName}.` } }))) as {
            notified?: Array<{ userId: string }>;
            reason?: string | null;
          };
          const n = (e.notified ?? []).length;
          const toCharge = !!charge && (e.notified ?? []).some((x) => x.userId === charge.userId);
          onToast(
            toCharge
              ? `Saved. ${charge!.name ?? 'The charge nurse'} has a request to check ${first(r)}.`
              : n
                ? `Saved. ${n === 1 ? 'One person' : `${n} people`} on duty ${n === 1 ? 'has' : 'have'} a request to check ${first(r)}.`
                : `Saved. ${e.reason ?? 'Nobody is on as charge nurse, so the building was alerted.'}`,
          );
        } else onToast('Saved to the chart.');
      } else if (cat === 'meals') {
        for (const id of sel) {
          const r = residents.find((x) => x.id === id)!;
          if (eaten || ml > 0) await step(`${meal} for ${r.name ?? 'a resident'}`, () => residentTimelineAdd(client, { params: { id }, body: { body: mealLine(meal, eaten, ml), kind: 'meal', discipline: 'care_staff' } }));
          if (ml > 0) await step(`fluids for ${r.name ?? 'a resident'}`, () => shiftFluid(client, { body: { residentId: id, ml } }));
        }
        onToast(sel.length > 1 ? `Saved for ${sel.length} residents.` : 'Saved to the chart.');
      } else {
        const r = residents.find((x) => x.id === sel[0])!;
        await step('daily care', () => residentTimelineAdd(client, { params: { id: r.id }, body: { body: adlLine(adl), kind: 'adl', discipline: 'care_staff' } }));
        onToast('Saved to the chart.');
      }
      onClose();
    } catch (e) {
      setError({ err: e instanceof ApiError ? e : new ApiError({ kind: 'network', status: 0, message: 'Aithora did not answer, so this was not saved.', method: 'POST', path: '' }), saved });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PocDialog
      title="Chart"
      onClose={onClose}
      testId="poc-chart"
      action={
        <A style={prim(ready)} onClick={() => void save()} disabled={!ready}>
          {sel.length > 1 ? `Save for ${sel.length} residents` : 'Save'}
        </A>
      }
    >
      <div role="group" aria-label="What to chart" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
        {(
          [
            ['vitals', 'Vitals'],
            ['meals', 'Meals & fluids'],
            ['adl', 'Daily care'],
          ] as const
        ).map(([k, l]) => (
          <A
            key={k}
            style={chip(cat === k)}
            onClick={() => {
              setCat(k);
              setSel((s) => (k === 'meals' ? s : s.slice(0, 1)));
              setError(null);
            }}
          >
            {l}
          </A>
        ))}
      </div>
      <div style={sx(HEAD)}>{multi ? 'Residents · pick one or more' : 'Resident'}</div>
      {residents.length ? (
        <div role="group" aria-label={multi ? 'Residents' : 'Resident'} style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
          {residents.map((r) => (
            <A key={r.id} style={chip(sel.includes(r.id))} onClick={() => pick(r.id)}>
              {residentLabel(r)}
            </A>
          ))}
        </div>
      ) : (
        <span role="status" style={sx('font-size:16px;color:#3C454F;')}>
          No residents are assigned to you this shift, so there is no one to chart for yet.
        </span>
      )}

      {cat === 'vitals' && (
        <>
          <div style={sx(HEAD)}>Vitals</div>
          <div style={sx(LIST)}>
            {VITALS.map((f) => {
              const flag = fieldFlag(f, vals[f.k]);
              return (
                <div key={f.k} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.08);')}>
                  <span style={sx('flex:1 1 160px;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{f.label}</span>
                  {flag && <span style={sx('font-size:16px;font-weight:700;color:#B3261E;')}>{flag}</span>}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={vals[f.k] ?? ''}
                    onChange={(e) => setVals((v) => ({ ...v, [f.k]: e.target.value.replace(/[^0-9.]/g, '') }))}
                    placeholder="–"
                    aria-label={f.label}
                    style={sx('flex:0 0 84px;width:84px;height:36px;box-sizing:border-box;padding:0 10px;border-radius:8px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;font-weight:700;text-align:right;color:#22272E;font-variant-numeric:tabular-nums;')}
                  />
                  <span style={sx('flex:0 0 44px;font-size:16px;color:#54606C;')}>{f.unit}</span>
                </div>
              );
            })}
          </div>
          {flags.length > 0 && (
            <div role="alert" data-testid="chart-flags" style={sx('padding:10px 12px;border-radius:9px;background:#FBEDEC;border:1px solid rgba(179,38,30,.4);font-size:16px;line-height:1.45;color:#8A1C16;font-weight:700;')}>
              {`When you save, ${chargeWho} and the administrator on duty get a request to check: ${flags.join(', ')}.`}
            </div>
          )}
        </>
      )}

      {cat === 'meals' && (
        <>
          <div style={sx(HEAD)}>Meal</div>
          <div role="group" aria-label="Meal" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {MEALS.map((x) => (
              <A key={x} style={chip(meal === x)} onClick={() => setMeal(x)}>
                {x}
              </A>
            ))}
          </div>
          <div style={sx(HEAD)}>How much they ate</div>
          <div role="group" aria-label="How much they ate" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {EATEN.map((x) => (
              <A key={x} style={chip(eaten === x, x === 'Refused')} onClick={() => setEaten(eaten === x ? '' : x)}>
                {x}
              </A>
            ))}
          </div>
          <div style={sx(HEAD)}>Fluids</div>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
            <span data-testid="chart-ml" style={sx('min-width:72px;font-size:18px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>{`${ml} mL`}</span>
            {[120, 240].map((n) => (
              <A key={n} style="display:inline-flex;align-items:center;min-height:36px;padding:0 13px;border-radius:18px;border:1px solid rgba(47,92,151,.45);background:#FFFFFF;font-size:16px;font-weight:700;color:#264C7E;text-decoration:none;white-space:nowrap;" onClick={() => setMl(ml + n)}>
                {`+${n} mL`}
              </A>
            ))}
            <A style="font-size:16px;font-weight:700;color:#54606C;text-decoration:none;" onClick={() => setMl(0)}>
              Reset
            </A>
          </div>
        </>
      )}

      {cat === 'adl' && (
        <>
          <div style={sx(HEAD)}>How much help this shift</div>
          <div style={sx(LIST)}>
            {ADLS.map((a) => (
              <div key={a} style={sx('display:flex;align-items:center;gap:10px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.08);')}>
                <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{a}</span>
                <select
                  value={adl[a] ?? ''}
                  onChange={(e) => setAdl((v) => ({ ...v, [a]: e.target.value }))}
                  aria-label={a}
                  style={sx('flex:0 0 180px;width:180px;height:36px;padding:0 8px;border-radius:8px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;color:#22272E;')}
                >
                  <option value="">Not charted</option>
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      {error && (
        <>
          <ErrorNote error={error.err} />
          {error.saved.length > 0 && <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.4;')}>{`Already saved: ${error.saved.join(', ')}. Nothing after that was sent.`}</span>}
        </>
      )}
      <span data-testid="chart-note" style={sx(NOTE)}>
        {cat === 'adl'
          ? 'How much help they needed this shift. It goes on their chart as a note; it does not fill in the MDS yet.'
          : cat === 'meals'
            ? 'Pick everyone at the same table and chart them in one go.'
            : 'Flag levels are Aithora’s defaults until your building sets its own. Breathing and pain are kept on the chart entry only.'}
      </span>
    </PocDialog>
  );
}
